import { Lightning, Sparkle, XCircle } from '@phosphor-icons/react';
import { useNavigate } from 'react-router';
import { Button } from '~/components/ui/button';
import { Tip } from '~/components/ui/tip';
import { toast } from 'sonner';
import { useReflectRuns, useReflectionEnabled } from '~/hooks/queries/use-reflect';
import { useRunCronNow } from '~/hooks/mutations/use-cron-mutations';
import type { CronJob } from '~/hooks/queries/use-crons';
import { formatRelativeTime } from '~/lib/relative-time';
import { REFLECTION_PATH, reflectionRowLabel } from './reflection-group';

/**
 * "Reflect now, just for me" through the reflect job's run-now route: it
 * reflects for the caller only (`runCronJobNow`'s reflect branch) and checks
 * the person's compute budget before the propose call
 * (`dispatchReflectPropose`). The run is queued, so the result lands in the
 * person's reflections when it finishes.
 */
export function useReflectNow() {
  const runNow = useRunCronNow();
  return {
    isPending: runNow.isPending,
    reflect: (jobId: string | undefined) => {
      if (!jobId) return;
      runNow.mutate(jobId, {
        onSuccess: () =>
          toast.success('Reflecting now', { description: 'Your reflection appears on the Reflection page when it finishes.' }),
        onError: (err) => toast.error(`Could not start a reflection: ${(err as Error).message}`),
      });
    },
  };
}

/**
 * FRONT-2439: the ONE Schedule row standing for every system reflection job.
 * Same shape as a `CronList` row, with the only action a user has on
 * reflection — reflect now, just for them. Nothing here pauses, edits,
 * deletes or re-authorizes: reflection is shared and self-authenticating,
 * and is switched off per person from its page.
 */
export function ReflectionRow({ crons, times }: { crons: CronJob[]; times: string[] }) {
  const count = crons.length;
  const navigate = useNavigate();
  const { data: enabled } = useReflectionEnabled();
  // Absent means on (the server default); only false means this person opted out.
  const off = enabled === false;
  const { data: runs } = useReflectRuns();
  const latest = runs?.[0];
  const lastFailed = latest?.status === 'failed';
  const lastRun = formatRelativeTime(latest?.createdAt);
  const runNow = useReflectNow();
  const label = reflectionRowLabel(count);

  return (
    <div
      className="group relative flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors overflow-hidden"
      data-testid="reflection-row"
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => navigate(REFLECTION_PATH)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate(REFLECTION_PATH);
          }
        }}
        className="flex-1 min-w-[220px] flex items-center gap-4 text-left cursor-pointer focus-visible:outline-none overflow-hidden"
      >
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${off ? 'bg-muted-foreground/40' : 'bg-success'}`} />
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-foreground truncate">{label}</span>
            <Tip label="EDDY reflects on your recent activity at these times each day. Open it to see your reflections or turn them off for your account.">
              <span className="flex items-center gap-1 text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground">
                <Sparkle size={10} weight="fill" /> Reflection
              </span>
            </Tip>
            {off && (
              <span className="text-[length:var(--text-2xs)] uppercase tracking-wider text-amber-600 dark:text-amber-400">
                Off for you
              </span>
            )}
          </div>
          <div className="mt-0.5 text-[length:var(--text-2xs)] text-muted-foreground truncate tabular-nums">
            {times.length > 0 ? times.join(' · ') : 'Daily'}
          </div>
        </div>
        {lastFailed && lastRun && (
          <div className="hidden sm:flex items-center gap-1.5 text-[length:var(--text-2xs)] text-destructive tabular-nums shrink-0">
            <XCircle size={11} />
            <Tip label="Your last reflection failed. Open it to see why."><span>{lastRun}</span></Tip>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="xs"
          onClick={() => runNow.reflect(crons[0]?.id)}
          disabled={runNow.isPending}
          tooltip="Reflect now, just for you"
          className="text-muted-foreground hover:text-foreground"
        >
          <Lightning size={13} weight="fill" /> Run now
        </Button>
      </div>
    </div>
  );
}
